import { createColumnHelper, flexRender } from "@tanstack/react-table";
import { useMemo } from "react";
import { ActionNames } from "tng-address-collection-shared/actionNames.ts";
import { fullName } from "tng-address-collection-shared/fullName.ts";
import {
  CardsListItem,
  GlobalSearchCardsItem,
} from "tng-address-collection-shared/types/apiTypes.ts";
import { MemoDetailButton } from "../components/DetailButton.tsx";
import { MemoDetailClickCell } from "../components/DetailClickCell.tsx";
import { MailToCell } from "../components/MailToCell.tsx";
import { ProjectLinkCell } from "../components/ProjectLinkCell.tsx";
import { SignaturesDisplay } from "../components/SignaturesDisplay.tsx";
import { useTranslator } from "../i18n/translator.ts";
import { Meta } from "./useSignatureListColumns.ts";
import { useUserHasAction } from "./useUserHasAction.ts";

export const useGlobalSearchColumns = (
  handleDetailClick: (row: GlobalSearchCardsItem) => void,
) => {
  const translator = useTranslator();
  const columnHelper = useMemo(
    () => createColumnHelper<GlobalSearchCardsItem>(),
    [],
  );
  const userHasAction = useUserHasAction();

  return useMemo(() => {
    const salutationMap: Record<CardsListItem["salutation"], string> = {
      male: translator("male"),
      female: translator("female"),
    };
    const formalityMap: Record<CardsListItem["formality"], string> = {
      formal: translator("formal"),
      informal: translator("informal"),
      "semi-formal": translator("semiFormal"),
    };

    return [
      {
        column: columnHelper.accessor("signatures", {
          id: "signatures",
          header: () => translator("cardTableHeaderSigned"),
          cell: ({ row, getValue }) =>
            flexRender(SignaturesDisplay, {
              cardId: row.original.id,
              projectId: row.original.projectId,
              signatures: getValue(),
              canEditSignatures: userHasAction(ActionNames.editCardSignatures),
              canRemoveOtherSignatures: true,
            }),
        }),
      },
      {
        column: columnHelper.accessor("title", {
          id: "title",
          header: () => translator("cardTableHeaderTitle"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor(fullName, {
          id: "name",
          header: () => translator("cardTableHeaderName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          size: 350,
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor((row) => row.email, {
          id: "email",
          header: () => translator("cardTableHeaderEmail"),
          cell: ({ getValue }) => flexRender(MailToCell, { email: getValue() }),
          size: 350,
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor(
          (row) => ({ id: row.projectId, name: row.projectDisplayName }),
          {
            id: "project",
            header: () => translator("cardTableHeaderProject"),
            cell: ({ getValue }) => flexRender(ProjectLinkCell, getValue()),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        column: columnHelper.accessor("language", {
          id: "language",
          header: () => translator("cardTableHeaderLanguage"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor("salutation", {
          id: "salutation",
          header: () => translator("cardTableHeaderSalutation"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: salutationMap[getValue()],
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor("formality", {
          id: "formality",
          header: () => translator("cardTableHeaderFormality"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: formalityMap[getValue()],
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor(
          (row) =>
            row.receivesGift
              ? translator("cardTableCellYes")
              : translator("cardTableCellNo"),
          {
            id: "receivesGift",
            header: () => translator("cardTableHeaderReceivesGift"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
              }),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        column: columnHelper.accessor("companyName", {
          id: "companyName",
          header: () => translator("cardTableHeaderCompanyName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor("departmentName", {
          id: "departmentName",
          header: () => translator("cardTableHeaderDepartmentName"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor("senderDisplayName", {
          id: "sender",
          header: () => translator("cardTableHeaderSender"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          meta: {} satisfies Meta,
        }),
      },
      {
        column: columnHelper.accessor(
          (row) =>
            row.manualDispatch
              ? translator("cardTableCellDispatchManual")
              : translator("cardTableCellDispatchAutomatic"),
          {
            id: "manualDispatch",
            header: () => translator("cardTableHeaderDispatch"),
            cell: ({ row, getValue }) =>
              flexRender(MemoDetailClickCell, {
                handleClick: () => handleDetailClick(row.original),
                value: getValue(),
                userHasAction,
              }),
            meta: {} satisfies Meta,
          },
        ),
      },
      {
        column: columnHelper.accessor("comment", {
          id: "comment",
          header: () => translator("cardTableHeaderComment"),
          cell: ({ row, getValue }) =>
            flexRender(MemoDetailClickCell, {
              handleClick: () => handleDetailClick(row.original),
              value: getValue(),
              userHasAction,
            }),
          // High value to ensure the other columns remain small
          size: 2_000,
          meta: {} satisfies Meta,
        }),
      },
      {
        hide: !userHasAction(ActionNames.editCard),
        column: columnHelper.display({
          id: "edit",
          header: "",
          cell: (info) =>
            flexRender(MemoDetailButton, {
              onDetailClick: () => handleDetailClick(info.row.original),
            }),
          size: 70,
          meta: { align: "center" } satisfies Meta,
        }),
      },
    ]
      .filter((x) => !x.hide)
      .map((x) => x.column);
  }, [columnHelper, userHasAction, translator, handleDetailClick]);
};
